import React from "react";

import { LineChart, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Line, ReferenceLine, Label } from "recharts";
import moment from "moment";

import keyExists from "../../../utils/keyExists";

import { Pictogram } from "selfly-ui-react";

import freeSpaceImage from "./freespace.svg";
import memoryImage from "./memory.svg";
// import temperatureImage from "./temperature.svg";
import cpuImage from "./cpu.svg";
// import timeImage from "./time.svg";

import "./DeviceHealthStatus.scss";

export default function DeviceHealthStatus(props) {
    const cpuTemp = keyExists("currentState.raw.cpu_temperature", props.device)
        ? props.device.currentState.raw.cpu_temperature
        : null;
    const cabinetTemp = keyExists("currentState.raw.temperature.temperature", props.device)
        ? props.device.currentState.raw.temperature.temperature
        : null;
    const cpuLoad = keyExists("currentState.raw.cpu_used", props.device)
        ? props.device.currentState.raw.cpu_used
        : null;
    const memUsed = keyExists("currentState.raw.memory_used", props.device)
        ? props.device.currentState.raw.memory_used
        : null;
    const diskUsed = keyExists("currentState.raw.fs_used", props.device) ? props.device.currentState.raw.fs_used : null;
    const uptime = keyExists("upTime.result", props.device) ? props.device.upTime.result : null;
    const online = keyExists("upTime.onlineTime", props.device) ? props.device.upTime.onlineTime : null;

    return (
        <div className="device-health-status">
            {cpuTemp !== null && cpuTemp !== "" && (
                <DeviceHealthParameter
                    title="CPU Temperature"
                    pictogram="hot"
                    value={cpuTemp}
                    valueType="temperature"
                    alarmLimit={60}
                    // history={getHistory(props.device, "raw.cpu_temperature")
                    history={getHistory(props.device, 0)
                }
                />
            )}

            {cabinetTemp !== null && cabinetTemp !== "" && (
                <DeviceHealthParameter
                    title="Cabinet Temperature"
                    pictogram="hot"
                    value={cabinetTemp}
                    valueType="temperature"
                    alarmLimit={10}
                    // history={getHistory(props.device, "raw.temperature.temperature")
                    history={getHistory(props.device, 4)}
                />
            )}

            <DeviceHealthParameter
                title="CPU Load"
                image={cpuImage}
                value={cpuLoad}
                valueType="percentage"
                alarmLimit={90}
                // history={getHistory(props.device, "raw.cpu_used")
                history={getHistory(props.device, 1)}
            />

            <DeviceHealthParameter
                title="Memory Used"
                image={memoryImage}
                value={memUsed}
                valueType="percentage"
                alarmLimit={80}
                // history={getHistory(props.device, "raw.memory_used")
                history={getHistory(props.device, 2)}
            />

            <DeviceHealthParameter
                title="Filesystem Used"
                image={freeSpaceImage}
                value={diskUsed}
                valueType="percentage"
                alarmLimit={80}
                // history={getHistory(props.device, "raw.fs_used")
                history={getHistory(props.device, 3)}
            />

            <DeviceHealthParameter
                title="Uptime since restart"
                // image={timeImage}
                pictogram="production_speed"
                value={uptime}
                valueType="percentage"
                history={[]}
            />

            <DeviceHealthParameter
                title="Online"
                // image={timeImage}
                pictogram="time_reports"
                value={online}
                valueType="percentage"
                history={[]}
            />
        </div>
    );
}

function getHistory(device, valueIndex) {
    if (Array.isArray(device.stateHistory)) {
        const valueHistory = device.stateHistory.map(history => {
            const val = history.values[valueIndex];
            // const val = keyExists(paramKey, history, false, true);
            const time = moment(history.timestamp);
            // console.log("time", timeStr, time, time.format('HH:mm'));

            return { value: val, ts: history.timestamp, clock: time.format("HH:mm") };
        });
        // console.log(`History for ${paramKey}`, valueHistory);
        return valueHistory.reverse();
    }
    return [];
}

function DeviceHealthParameter(props) {
    const { value, valueType, title, image, pictogram } = props;

    let dispValue = "";

    if (value === null) {
        dispValue = <p className="value">N/A</p>;
    } else {
        switch (valueType) {
            case "temperature":
                if (isNaN(value) || value === "") {
                    dispValue = <p className="error">Err</p>;
                    console.warn(
                        `Expected ${title} value to be a ${valueType} number.\n\tvalue was: `,
                        value,
                        "\n\tType was:",
                        typeof value
                    );
                } else {
                    const rValue = Math.round(value);
                    dispValue = (
                        <p className="value">
                            {rValue}
                            <span>{"\xB0"}C</span>
                        </p>
                    );
                }
                break;
            case "percentage":
                if (isNaN(value)) {
                    dispValue = <p className="error">Err</p>;
                    console.warn(
                        `Expected ${title} value to be a ${valueType} number.\n\tvalue was: `,
                        value,
                        "\n\tType was:",
                        typeof value
                    );
                } else {
                    const rValue = Math.round(value);
                    dispValue = (
                        <p className="value">
                            {rValue}
                            <span>%</span>
                        </p>
                    );
                }

                break;
            default:
                if (value === undefined || value === null) {
                    dispValue = <p className="error">Err</p>;
                }
                dispValue = <p>{value}</p>;
                break;
        }
    }

    return (
        <div className="device-health-parameter">
            <div className="parameter-icon">
                {image !== undefined && <img src={image} alt={title} />}
                {pictogram !== undefined && <Pictogram name={pictogram} />}
            </div>
            <div className="parameter-value">
                <label>{title}</label>
                {dispValue}
            </div>
            <div className="parameter-graph">
                {props.history === undefined || props.history.length === 0 ? (
                    <p className="no-history">No historical data available</p>
                ) : valueType === "temperature" ? (
                    <TemperatureChart data={props.history} alarmLimit={props.alarmLimit} />
                ) : (
                    <PercentageChart data={props.history} alarmLimit={props.alarmLimit} />
                )}
            </div>
        </div>
    );
}

function TemperatureChart(props) {
    const { data, alarmLimit } = props;
    const ticks =
        process.env.REACT_APP_GRAPH_HISTORY_TICKS !== undefined ? process.env.REACT_APP_GRAPH_HISTORY_TICKS : 100;
    const interval = Math.round(ticks / 15);
    
    return (
        <ResponsiveContainer className="device-graph-container" width="100%" height={100}>
            <LineChart data={data}>
                <CartesianGrid stroke="#e1e2e3" />
                <ReferenceLine y={alarmLimit} stroke="red" strokeDasharray="3 3">
                    <Label className="reference-label">{alarmLimit}&deg;</Label>
                </ReferenceLine>
                <XAxis
                    dataKey="clock"
                    domain={["auto", "auto"]}
                    name="Time"
                    tick={{ fontSize: 9 }}
                    stroke="#8a8a8a"
                    interval={interval}
                />

                <YAxis tick={{ fontSize: 9 }} type="number" domain={[0, "auto"]} stroke="#8a8a8a" unit="&deg;" />
                <Line type="monotone" dataKey="value" stroke="#28b6ea" strokeWidth={2} dot={false} activeDot={false} />
                {/* <Tooltip content={<CustomToolTip alarmLimit={alarmLimit} />} /> */}
            </LineChart>
        </ResponsiveContainer>
    );
}

function PercentageChart(props) {
    const { data, alarmLimit } = props;
    const ticks =
        process.env.REACT_APP_GRAPH_HISTORY_TICKS !== undefined ? process.env.REACT_APP_GRAPH_HISTORY_TICKS : 500;
    const interval = Math.round(ticks / 15);

    const limitText = `${alarmLimit}%`;
    return (
        <ResponsiveContainer className="device-graph-container" width="100%" height={100}>
            <LineChart data={data}>
                <CartesianGrid stroke="#e1e2e3" />

                <ReferenceLine y={alarmLimit} stroke="red" strokeDasharray="3 3">
                    <Label className="reference-label" value={limitText} />
                </ReferenceLine>
                <XAxis
                    dataKey="clock"
                    domain={["auto", "auto"]}
                    name="Time"
                    tick={{ fontSize: 9 }}
                    stroke="#8a8a8a"
                    tickCount={10}
                    interval={interval}
                />

                <YAxis tick={{ fontSize: 9 }} type="number" domain={[0, 100]} stroke="#8a8a8a" unit="%" />
                <Line type="monotone" dataKey="value" stroke="#28b6ea" dot={false} strokeWidth={2} />
                {/* <Tooltip content={<CustomToolTip alarmLimit={alarmLimit} />} /> */}
            </LineChart>
        </ResponsiveContainer>
    );
}

